/**
 * Granny's routine - the activity scheduler.
 *
 * THIS IS THE LEVEL DESIGN (see DESIGN.md 1.4). Each activity is a different
 * stealth WINDOW with its own blindness, deafness, mobility and reaction delay.
 * Reading and exploiting her schedule is the actual skill the game teaches, so
 * these profiles are the most important balance data in the project after the
 * heat curve.
 *
 * Kept separate from granny.ts so that "what she does when calm" and "how she
 * hunts" stay independently editable. An agent retuning her routine should never
 * need to open the chase code.
 */

import type { ActivityDef } from '@/levels/schema';
import type { Vec3 } from '@/core/math';
import { distanceXZ, vec3, yawTowards } from '@/core/math';
import type { Rng } from '@/core/rng';
import { tuning } from '@/core/tuning';

export interface ActivityProfile {
  id: string;
  label: string;
  /** Scales her field of view. 0 means completely blind. */
  visionFovScale: number;
  /** Scales her sight range. */
  visionRangeScale: number;
  /** Scales incoming loudness. 0 means completely deaf. */
  hearingScale: number;
  /** She stays at the anchor rather than wandering. */
  stationary: boolean;
  /** How she orients: at the anchor's target, sweeping, or turning randomly. */
  facing: 'anchor' | 'sweep' | 'random';
  /**
   * Seconds before she can react to a stimulus. This is the mechanical heart of
   * the design: VR gives you a long grace period, watering plants gives you none.
   */
  interruptDelay: number;
}

/**
 * The profile table. Tuned so that every activity is a distinctly different
 * puzzle rather than a numeric variation on the same one.
 */
export const ACTIVITY_PROFILES: Record<string, ActivityProfile> = {
  water_plants: {
    id: 'water_plants',
    label: 'Watering the plants',
    // Poor window on purpose: she is actively looking around at her plants.
    visionFovScale: 1.0,
    visionRangeScale: 1.0,
    hearingScale: 1.0,
    stationary: false,
    facing: 'sweep',
    interruptDelay: 0.15,
  },
  cooking: {
    id: 'cooking',
    label: 'Cooking',
    // Facing the stove, but her ears are sharp in her own kitchen.
    visionFovScale: 0.55,
    visionRangeScale: 0.7,
    hearingScale: 1.4,
    stationary: true,
    facing: 'anchor',
    interruptDelay: 0.4,
  },
  watch_tv: {
    id: 'watch_tv',
    label: 'Watching TV',
    visionFovScale: 0.5,
    visionRangeScale: 0.8,
    // The TV masks quiet sounds.
    hearingScale: 0.6,
    stationary: true,
    facing: 'anchor',
    interruptDelay: 0.8,
  },
  eating: {
    id: 'eating',
    label: 'Eating',
    visionFovScale: 0.45,
    visionRangeScale: 0.6,
    hearingScale: 0.9,
    stationary: true,
    facing: 'anchor',
    interruptDelay: 0.6,
  },
  vr_headset: {
    id: 'vr_headset',
    label: 'Playing VR',
    // THE MARQUEE WINDOW. Completely blind and deaf, flailing in place.
    // Level 4 is built around this. Bumping her ends it instantly at +30 heat.
    visionFovScale: 0,
    visionRangeScale: 0,
    hearingScale: 0,
    stationary: true,
    facing: 'random',
    interruptDelay: 2.2,
  },
  nap: {
    id: 'nap',
    label: 'Napping',
    // Best window, highest risk: a loud noise wakes her straight into Suspicious.
    visionFovScale: 0,
    visionRangeScale: 0,
    hearingScale: 0.75,
    stationary: true,
    facing: 'anchor',
    interruptDelay: 1.6,
  },
  phone_call: {
    id: 'phone_call',
    label: 'On the phone',
    // The wildcard: distracted, but wandering unpredictably through the level.
    visionFovScale: 0.8,
    visionRangeScale: 0.85,
    hearingScale: 0.7,
    stationary: false,
    facing: 'random',
    interruptDelay: 0.5,
  },
  tidy_up: {
    id: 'tidy_up',
    label: 'Tidying up',
    visionFovScale: 0.9,
    visionRangeScale: 0.9,
    hearingScale: 1.0,
    stationary: false,
    facing: 'sweep',
    interruptDelay: 0.2,
  },
};

/* ------------------------------------------------------------------ */
/* Presentation                                                        */
/* ------------------------------------------------------------------ */

export type HearingBand = 'sharp' | 'normal' | 'dulled' | 'deaf';

export interface ActivityPresentation {
  /** Carries the whole meaning on its own. Assume the player cannot read. */
  icon: string;
  /** Backup only, never the sole signal. Three words maximum. */
  label: string;
  canSee: boolean;
  hearing: HearingBand;
}

/**
 * Icons for every activity, so a child can read her schedule.
 *
 * Reading and exploiting the schedule is THE skill the game teaches (DESIGN.md
 * §4), and until this existed the player got exactly one bit of information: the
 * cone vanished when she was blind. Napping and watching TV looked identical.
 *
 * Derived from the profile rather than hardcoded per activity, so a new entry in
 * ACTIVITY_PROFILES cannot ship invisible - only its icon and label are authored.
 */
const ACTIVITY_ICONS: Record<string, { icon: string; label: string }> = {
  water_plants: { icon: '🪴', label: 'Watering' },
  cooking: { icon: '🍳', label: 'Cooking' },
  watch_tv: { icon: '📺', label: 'Watching TV' },
  eating: { icon: '🍽️', label: 'Eating' },
  vr_headset: { icon: '🥽', label: 'Playing VR' },
  nap: { icon: '💤', label: 'Napping' },
  phone_call: { icon: '📞', label: 'On the phone' },
  tidy_up: { icon: '🧹', label: 'Tidying' },
  // Not routine activities: the two profiles granny.ts substitutes when she is
  // off her schedule. They belong here so an interrupted granny reads as
  // "stopped and looking" rather than still watering her plants - and so a
  // granny stooping to say hello never reads as a threat.
  alerted: { icon: '❗', label: 'Alerted' },
  petting: { icon: '💕', label: 'Saying hello' },
};

export function hearingBandFor(
  hearingScale: number,
  thresholds = tuning.granny.presentation,
): HearingBand {
  if (hearingScale <= 0) return 'deaf';
  if (hearingScale >= thresholds.hearingSharpThreshold) return 'sharp';
  if (hearingScale <= thresholds.hearingDullThreshold) return 'dulled';
  return 'normal';
}

/**
 * How an activity should read on screen.
 *
 * `canSee` is taken from the same `visionFovScale` the perception code uses, so
 * the closed-eye icon and the missing vision cone can never disagree - a lying
 * indicator would be worse than no indicator at all.
 */
export function presentationFor(
  profile: ActivityProfile,
  thresholds = tuning.granny.presentation,
): ActivityPresentation {
  const authored = ACTIVITY_ICONS[profile.id];
  return {
    icon: authored?.icon ?? '❓',
    label: authored?.label ?? profile.label,
    canSee: profile.visionFovScale > 0 && profile.visionRangeScale > 0,
    hearing: hearingBandFor(profile.hearingScale, thresholds),
  };
}

/** Every activity that has an authored icon. Used to prove none is missing. */
export function activityIdsWithPresentation(): string[] {
  return Object.keys(ACTIVITY_ICONS);
}

/** Falls back to a fully-alert profile so an unknown id is safe, if unhelpful. */
export function profileFor(activityId: string): ActivityProfile {
  return (
    ACTIVITY_PROFILES[activityId] ?? {
      id: activityId,
      label: activityId,
      visionFovScale: 1,
      visionRangeScale: 1,
      hearingScale: 1,
      stationary: true,
      facing: 'anchor',
      interruptDelay: 0.2,
    }
  );
}

export type RoutinePhase = 'travelling' | 'performing';

export class RoutineScheduler {
  private index = -1;
  private elapsed = 0;
  /** Time spent walking at the current anchor without reaching it. */
  private travelElapsed = 0;
  /** Anchors she gave up on, so the next pick is not the same wall. */
  private readonly unreachable = new Set<number>();
  phase: RoutinePhase = 'travelling';
  /** Set while she is paused mid-activity by an interruption. */
  private pausedElapsed: number | null = null;

  constructor(
    private readonly activities: ActivityDef[],
    private readonly rng: Rng,
  ) {}

  get currentActivity(): ActivityDef | undefined {
    return this.activities[this.index];
  }

  get currentProfile(): ActivityProfile {
    const activity = this.currentActivity;
    return profileFor(activity?.activityId ?? 'tidy_up');
  }

  /** Where she is heading or standing. */
  get anchor(): Vec3 {
    const activity = this.currentActivity;
    if (!activity) return vec3();
    return vec3(activity.anchor[0], activity.anchor[1], activity.anchor[2]);
  }

  /** Yaw she should hold while performing. */
  facingYaw(from: Vec3, timeInActivity: number): number {
    const activity = this.currentActivity;
    const profile = this.currentProfile;

    if (profile.facing === 'anchor' && activity?.faceTowards) {
      const target = vec3(
        activity.faceTowards[0],
        activity.faceTowards[1],
        activity.faceTowards[2],
      );
      return yawTowards(from, target);
    }
    if (profile.facing === 'sweep') {
      // Slow left-right sweep. This is what makes "watering the plants" a poor
      // hiding window - she is constantly re-checking her surroundings.
      const base = activity?.faceTowards
        ? yawTowards(
            from,
            vec3(activity.faceTowards[0], activity.faceTowards[1], activity.faceTowards[2]),
          )
        : 0;
      return base + Math.sin(timeInActivity * 0.7) * 0.9;
    }
    // 'random': lurches around, e.g. VR flailing.
    return Math.sin(timeInActivity * 1.9) * 2.4 + Math.sin(timeInActivity * 0.6) * 1.1;
  }

  /** Picks the next activity, weighted, avoiding an immediate repeat. */
  advance(): void {
    if (this.activities.length === 0) return;
    if (this.activities.length === 1) {
      this.index = 0;
    } else {
      // Skip anchors she has already failed to reach. If every one has failed,
      // fall back to the full list rather than freezing - a granny retrying a
      // bad anchor forever is still better than a granny doing nothing at all,
      // and it keeps a mis-authored level obviously broken instead of silently
      // inert.
      const reachable = this.activities.filter(
        (_, i) => i !== this.index && !this.unreachable.has(i),
      );
      const candidates =
        reachable.length > 0 ? reachable : this.activities.filter((_, i) => i !== this.index);
      const chosen = this.rng.pickWeighted(candidates, (a) => a.weight ?? 1);
      this.index = this.activities.indexOf(chosen);
    }
    this.elapsed = 0;
    this.travelElapsed = 0;
    this.phase = 'travelling';
  }

  /** Anchors she gave up walking to. Surfaced so a level test can assert none. */
  get unreachableAnchorCount(): number {
    return this.unreachable.size;
  }

  /**
   * One step of the routine. Returns the position she should move toward, or
   * null when she has arrived and is performing.
   */
  step(position: Vec3, dt: number, arriveRadius: number): Vec3 | null {
    if (this.index < 0) this.advance();
    const activity = this.currentActivity;
    if (!activity) return null;

    if (this.phase === 'travelling') {
      if (distanceXZ(position, this.anchor) <= arriveRadius) {
        this.phase = 'performing';
        this.elapsed = 0;
        this.travelElapsed = 0;
        return null;
      }

      // She has no pathfinder: she walks straight at the anchor and slides along
      // whatever she hits. An anchor behind the couch is unreachable, and the
      // routine only counts down while PERFORMING - so without this she pushes
      // into the furniture forever and her whole schedule stops. That stall was
      // real, and it looked exactly like a granny glued to a bookshelf.
      this.travelElapsed += dt;
      if (this.travelElapsed >= tuning.granny.navigation.routineTravelTimeoutSeconds) {
        this.unreachable.add(this.index);
        this.advance();
        return this.anchor;
      }
      return this.anchor;
    }

    this.elapsed += dt;
    if (this.elapsed >= activity.durationSeconds) this.advance();
    return null;
  }

  get timeInActivity(): number {
    return this.elapsed;
  }

  /** Remembers progress so an interruption does not restart the activity. */
  pause(): void {
    if (this.pausedElapsed === null) this.pausedElapsed = this.elapsed;
  }

  /** Restores progress after an interruption resolves. */
  resume(): void {
    if (this.pausedElapsed !== null) {
      this.elapsed = this.pausedElapsed;
      this.pausedElapsed = null;
      // She has to walk back to whatever she was doing.
      this.phase = 'travelling';
    }
  }

  reset(): void {
    this.index = -1;
    this.elapsed = 0;
    this.travelElapsed = 0;
    this.phase = 'travelling';
    this.pausedElapsed = null;
    // Deliberately keeps `unreachable`: geometry does not move between resets,
    // and re-learning the same blocked anchor costs another eight seconds of
    // her standing in the furniture.
  }
}
